import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import CalendarPlus from "@/assets/icon/svgs/CalendarPlus.svg";
import { SelectWithPlaceHolder } from "../customComponents/SelectWithPlaceHolder";
import HouseManager from "@/api-client/api/services/houseManager/houseManager";

export function OpenPeriod({
  trigger,
  setPeriodMonthYear,
  selectedBuildAddress,
  setSelectedPeriodOptions,
}: {
  trigger: React.ReactNode;
  setPeriodMonthYear: (value: string) => void;
  selectedBuildAddress: { label: string; value: string };
  setSelectedPeriodOptions: (value: { name: string; value: string }) => void;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const now = new Date();
  const [month, setMonth] = useState<string>(
    String(now.getMonth() + 1).padStart(2, "0")
  );
  const [year, setYear] = useState<string>(String(now.getFullYear()));

  const handleDialogClose = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const createInvoicePeriod = async () => {
    const date = `${year}-${month}`;

    try {
      const createdInvoicePeriod = await HouseManager.postGenerateInvoices(
        date,
        selectedBuildAddress.value
      );
      setIsOpen(false);
      setPeriodMonthYear(date);
      setSelectedPeriodOptions({
        name: t("house_manager.current_period"),
        value: "current_period",
      });
      return createdInvoicePeriod;
    } catch (error) {
      console.error("Error fetching buildings:", error);
    }
  };

  const months = [
    { value: "01", name: t("monthsTranslations.01") },
    { value: "02", name: t("monthsTranslations.02") },
    { value: "03", name: t("monthsTranslations.03") },
    { value: "04", name: t("monthsTranslations.04") },
    { value: "05", name: t("monthsTranslations.05") },
    { value: "06", name: t("monthsTranslations.06") },
    { value: "07", name: t("monthsTranslations.07") },
    { value: "08", name: t("monthsTranslations.08") },
    { value: "09", name: t("monthsTranslations.09") },
    { value: "10", name: t("monthsTranslations.10") },
    { value: "11", name: t("monthsTranslations.11") },
    { value: "12", name: t("monthsTranslations.12") },
  ];

  const years = [2020, 2021, 2022, 2023, 2024, 2025].map((year) => ({
    value: year.toString(),
    name: `${year}`,
  }));

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose} modal={false}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      {isOpen && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-[4px] z-40 pointer-events-none"></div>
      )}
      <DialogContent
        className="max-h-full overflow-auto w-[440px] pb-[40px]"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader className="mb-[16px]">
          <DialogTitle className="text-[24px] font-medium flex items-center gap-4">
            <img src={CalendarPlus} alt="icon" className="w-[32px] h-[32px]" />
            {t("house_manager.open_a_period")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[16px] font-medium mb-4">
          {selectedBuildAddress.label}
        </p>
        <div className="flex gap-6 mb-4">
          <div className="w-[70%]">
            <SelectWithPlaceHolder
              placeholder={t("Month")}
              selectValue={month}
              onSelectChange={(val) => setMonth(val)}
              selectValues={months}
            />
          </div>

          <div className="w-[30%]">
            <SelectWithPlaceHolder
              placeholder={t("Year")}
              selectValue={year}
              onSelectChange={(val) => setYear(val)}
              selectValues={years}
            />
          </div>
        </div>

        <Button
          type="submit"
          variant="system"
          size="system"
          disabled={false}
          loading={false}
          onClick={() => {
            createInvoicePeriod();
          }}
        >
          {t("confirm")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
